/**
 * /table · query intent classifier — UI-side mirror of the backend
 * `expand_query_tokens` envelope. We can't await the network for the
 * "understood as …" line so this is a deterministic frontend echo.
 *
 * It's a courtesy hint only; the real intent the backend used is
 * always sent back as `response.interpreted` and we prefer that when
 * we have it.
 */

const CITIES = [
    "bangkok", "bangalore", "bengaluru", "mumbai", "delhi", "new delhi",
    "kolkata", "chennai", "hyderabad", "pune", "goa", "ooty", "coonoor",
    "jaipur", "udaipur",
    "paris", "london", "tokyo", "kyoto", "osaka", "lima", "lisbon",
    "barcelona", "madrid", "berlin", "vienna", "rome", "milan",
    "dubai", "abu dhabi", "doha", "istanbul", "singapore", "hong kong",
    "shanghai", "seoul", "sydney", "melbourne", "new york", "manhattan",
    "san francisco", "los angeles", "chicago", "miami", "boston",
];

const CUISINES = [
    "italian", "chinese", "japanese", "korean", "thai", "vietnamese",
    "indian", "french", "spanish", "mexican", "american", "british",
    "greek", "turkish", "lebanese", "moroccan", "mediterranean",
    "middle eastern", "peruvian", "pan asian", "pan-asian",
    "sushi", "ramen", "omakase", "kaiseki", "tandoor", "biryani",
    "dim sum", "pizza", "pasta", "tapas", "bbq", "barbecue",
    "steakhouse", "seafood", "vegetarian", "vegan",
];

const PRICE_REGEX = /\${1,4}|£+|under|over|michelin|star|budget|cheap|fine dining|tasting menu/i;

export function classifyQuery(rawQuery) {
    const q = (rawQuery || "").toLowerCase().trim();
    if (!q) return null;

    const cuisinesHit = CUISINES.filter((c) => q.includes(c));
    const citiesHit = CITIES.filter((c) => q.includes(c));
    const priceHit = PRICE_REGEX.test(q);

    // Venue intent: capitalised proper noun that isn't a city / cuisine.
    const tokens = (rawQuery || "")
        .replace(/[",.()]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    const filler = new Set([
        "the", "a", "an", "in", "at", "near", "with", "and", "or", "of",
        "by", "for", "on", "to", "best", "top", "good", "great",
        "food", "restaurant", "restaurants", "dinner", "lunch",
        "breakfast", "brunch", "casual", "fine", "michelin", "star",
        "under", "over",
    ]);
    const venueTokens = tokens.filter((t) => {
        const tl = t.toLowerCase();
        if (filler.has(tl)) return false;
        if (cuisinesHit.includes(tl)) return false;
        if (citiesHit.some((c) => c.split(/\s+/).includes(tl))) return false;
        if (/^[$£]+$/.test(t)) return false;
        if (/^\d+$/.test(t)) return false;
        // Strong venue signal: starts with a capital letter
        return /^[A-Z]/.test(t);
    });

    if (venueTokens.length >= 1 && cuisinesHit.length === 0) {
        return { kind: "venue", label: "Exact venue", detail: venueTokens.join(" ") + (citiesHit[0] ? `, ${citiesHit[0]}` : "") };
    }
    if (cuisinesHit.length > 0 && priceHit) {
        return { kind: "refined", label: "Cuisine + budget", detail: `${cuisinesHit.join(" + ")}${citiesHit[0] ? " · " + citiesHit[0] : ""}` };
    }
    if (cuisinesHit.length > 0 && citiesHit.length > 0) {
        return { kind: "cuisinecity", label: "Cuisine in a city", detail: `${cuisinesHit.join(" + ")} · ${citiesHit[0]}` };
    }
    if (citiesHit.length > 0) {
        return { kind: "destination", label: "Destination", detail: citiesHit[0] };
    }
    if (cuisinesHit.length > 0) {
        return { kind: "cuisine", label: "Cuisine", detail: cuisinesHit.join(" + ") };
    }
    return { kind: "open", label: "Open search", detail: rawQuery.trim().slice(0, 28) };
}

/**
 * Prefer the backend's interpreted envelope when we have one. Falls
 * back to the local heuristic. Returns the same {kind,label,detail}
 * shape the UI consumes.
 */
export function envelopeToIntent(envelope, fallbackQuery) {
    if (!envelope) return classifyQuery(fallbackQuery);
    const cuisines = envelope.cuisine_tokens || [];
    const places = envelope.place_tokens || [];
    const price = envelope.price_band;
    const venues = envelope.venue_tokens || [];
    if (venues.length > 0 && cuisines.length === 0) {
        return { kind: "venue", label: "Exact venue", detail: venues.concat(places).join(" · ") };
    }
    if (cuisines.length > 0 && price) {
        return { kind: "refined", label: "Cuisine + budget", detail: `${cuisines.join(" + ")} · ${places.join(", ")}${price ? " · " + price : ""}`.replace(" · ", " · ") };
    }
    if (cuisines.length > 0 && places.length > 0) {
        return { kind: "cuisinecity", label: "Cuisine in a city", detail: `${cuisines.join(" + ")} · ${places.join(", ")}` };
    }
    if (places.length > 0) {
        return { kind: "destination", label: "Destination", detail: places.join(", ") };
    }
    if (cuisines.length > 0) {
        return { kind: "cuisine", label: "Cuisine", detail: cuisines.join(" + ") };
    }
    return classifyQuery(fallbackQuery);
}
